import React, {
    useState,
    useMemo,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { Typography, Button } from '@comfama/comfama-ui-react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { DataTable } from '@/components/organisms/data-table/DataTable';
import CabeceraLibranzas from '@/components/organisms/cabecera-libranzas/cabeceraLibranzas';
import LoginRequest from '@/components/organisms/login-request/login-request';
import GlobalContext from '@/contexts/globalContext';
import BusinessModal from '@/components/molecules/bussiness-modal/businessModal';
import LibranzaCargaMasiva from '@/components/molecules/libranzas/cargaMasiva';
import PaymentStepper from '@/components/molecules/payment-stepper/payment-stepper';
import LibranzasResumenPago from '@/components/organisms/libranzas-resumen-pago/libranzasResumenPago';
import type { PaymentHeadInfo } from '@/components/molecules/libranzas/libranzasAccionesPago/libranzasAccionesPago';
import LibranzasConfirmacionPago from '@/components/molecules/libranzas/libranzasConfirmacionPago/libranzasConfirmacionPago';
import LibranzasHistorial from '@/components/organisms/libranzas-historial/libranzasHistorial';
import GenericToast from '@/components/atoms/generic-toast/genericToast';
import Modal from '@/components/atoms/modal/modal';
import Loader from '@/components/atoms/loader/loader';
import { getAssetUrl } from '@/utils/contentFullAsset';
import { formatCurrency, isValidEmail } from '@/utils/libranzasFormat';
import './pagoCreditosLibranzasTemplate.scss';
import { getLibranzasEmpresas } from '@/services/getLibranzasEmpresas';
import { getExcelLibranzas } from '@/services/getExcelLibranzas';
import { downloadHistorialExcelLibranzas } from '@/services/downloadHistorialExcelLibranzas';
import { adaptLibranzasCreditosToRows, mergeCargarExcelFilasIntoRows } from '@/components/organisms/data-table/datatable.adapter';
import { useLibranzasPago } from '@/hooks/useLibranzasPago';
import { useHistorialLibranzas } from '@/hooks/useHistorialLibranzas';
import { useErroresLibranzasDictionary, resolveLibranzasErrorMessage } from '@/features/libranzas-errores';

interface Referencia {
    nombre?: string;
    subtitulo?: string;
    lista?: string[];
    boton?: Array<{ copy?: string; redireccion?: string }>;
    interrogationDescription?: { raw: string };
}

interface PagoCreditosLibranzasTemplateProps {
    queryData: {
        allContentfulTemplate: {
            nodes: Array<{
                descripcion: {
                    raw: string;
                    references?: Array<{
                        contentful_id: string;
                        title?: string;
                        file?: { url: string };
                    }>;
                };
                imagen: Array<{
                    title: string;
                    file: { url: string };
                }>;
                migaDePan: string;
                nombre?: string;
                serviceId: string;
                interrogationDescription?: { raw: string };
                referencias?: Referencia[];
            }>;
        };
    };
    queryDataRealizados?: {
        allContentfulTemplate: {
            nodes: Array<{
                estaActivo: boolean;
                migaDePan: string;
                titulo: string;
                nombre: string;
                imagen: Array<{
                    title: string;
                    file: { url: string };
                }>;
                descripcion: { raw: string };
            }>;
        };
    };
}

const PLACETOPAY_QUERY = graphql`
    query LibranzasPlacetopayMetodosPago {
        placetopay: allContentfulTemplate(
            filter: { titulo: { eq: "Elige el medio de pago que prefieras > Placetopay" } }
        ) {
            nodes {
                nombre
                subtitulo
                referencias {
                    ... on ContentfulTemplate {
                        nombre
                        subtitulo
                        imagen {
                            title
                            file {
                                url
                            }
                        }
                    }
                }
            }
        }
        infoLibranzas: allContentfulTemplate(
            filter: { titulo: { eq: "Libranzas > Información del paso de pago" } }
        ) {
            nodes {
                nombre
                subtitulo
                referencias {
                    ... on ContentfulTemplate {
                        nombre
                        subtitulo
                        imagen {
                            title
                            file {
                                url
                            }
                        }
                    }
                }
            }
        }
        modalConfirmacion: allContentfulTemplate(
            filter: { titulo: { eq: "Modal de redirección a pagos Comfama" } }
        ) {
            nodes {
                nombre
                imagen {
                    url
                }
                subtitulo
                referencias {
                    ... on ContentfulTemplate {
                        nombre
                    }
                }
            }
        }
    }
`;

export const PagoCreditosLibranzasTemplate = ({
    queryData,
    queryDataRealizados,
}: PagoCreditosLibranzasTemplateProps) => {
    const { business, gigyaContext } = useContext(GlobalContext);

    const placeToPayData = useStaticQuery(PLACETOPAY_QUERY);
    const placeToPayNode = placeToPayData?.placetopay?.nodes?.[0];
    const paymentMethodRefs: Array<{ nombre?: string; subtitulo?: string; imagen?: Array<{ title?: string; file?: { url?: string } }> }> =
        placeToPayNode?.referencias ?? [];
    // Contentful entrega las URLs de asset sin protocolo ("//images.ctfassets.net/...")
    const toHttpsUrl = (url?: string) =>
        url ? (url.startsWith('//') ? `https:${url}` : url) : '';

    const confirmacionNode = placeToPayData?.modalConfirmacion?.nodes?.[0];
    const confirmacionTitulo: string = confirmacionNode?.nombre ?? '';
    const confirmacionDescripcion: string = confirmacionNode?.subtitulo ?? '';
    const confirmacionImagenUrl = toHttpsUrl(confirmacionNode?.imagen?.[0]?.url);
    // errorCode==='' (éxito) y errorCode===2 (pago repetido) comparten la misma modal y el mismo
    // contenido/botones de Contentful ("Modal de redirección a pagos Comfama") — el caso de pago
    // repetido solo agrega el texto de advertencia fijo debajo del título.
    const confirmacionBotones: { cancelar: string; continuar: string } = {
        cancelar: confirmacionNode?.referencias?.[0]?.nombre ?? 'Cancelar',
        continuar: confirmacionNode?.referencias?.[1]?.nombre ?? 'Continuar',
    };
    const PAGO_REPETIDO_ADVERTENCIA =
        'Realizaste un pago a la misma referencia en las últimas 24 horas. ¿Estas seguro de continuar con la transacción?';

    const infoLibranzasNode = placeToPayData?.infoLibranzas?.nodes?.[0];
    const paymentHeadInfo: PaymentHeadInfo | undefined = infoLibranzasNode
        ? {
              nombre: infoLibranzasNode.nombre,
              subtitulo: infoLibranzasNode.subtitulo,
              pasos: infoLibranzasNode.referencias,
          }
        : undefined;

    const [empresaSeleccionada, setEmpresaSeleccionada] = useState(
        () =>
            !!(
                business &&
                business.get &&
                business.get.companyDocID &&
                business.get.companyDocType
            )
    );

    const [showLogin, setShowLogin] = useState(false);

    const templateData = queryData?.allContentfulTemplate?.nodes?.[0];
    const descripcionRaw = templateData?.descripcion?.raw || '';

    const templateDataRealizados = queryDataRealizados?.allContentfulTemplate?.nodes?.[0];

    const richTextOptions = useMemo(() => ({
        renderNode: {
            [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => (
                <a
                    href={node.data.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {children}
                </a>
            ),
            [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
                const references = (templateData?.descripcion as any)
                    ?.references;
                const asset = references?.find(
                    (ref: any) => ref.contentful_id === node.data.target.sys.id
                );
                if (!asset) return null;
                const src = asset.file?.url;
                return src ? (
                    <img
                        src={src.startsWith('//') ? `https:${src}` : src}
                        alt={asset.title || ''}
                    />
                ) : null;
            },
        },
    }), [templateData?.descripcion]);

    const descripcion = descripcionRaw
        ? documentToReactComponents(JSON.parse(descripcionRaw), richTextOptions)
        : null;
    const nombre = templateData?.nombre;
    const imgInfo = templateData?.imagen?.[0];
    const fileUrl = getAssetUrl(imgInfo);
    const imgTitle = imgInfo?.title;
    const breadcrumbData = templateData?.migaDePan || '';
    const interrogationDescription = templateData?.interrogationDescription?.raw
        ? documentToReactComponents(
              JSON.parse(templateData.interrogationDescription.raw),
              richTextOptions
          )
        : null;

    const realizadosImgInfo = templateDataRealizados?.imagen?.[0];
    const realizadosImagenUrl = getAssetUrl(realizadosImgInfo);
    const realizadosImgTitle = realizadosImgInfo?.title;
    const realizadosDescripcion = templateDataRealizados?.descripcion?.raw
        ? documentToReactComponents(
              JSON.parse(templateDataRealizados.descripcion.raw),
              richTextOptions
          )
        : null;

    const [openBusinessModal, setOpenBusinessModal] = useState(false);

    const [rowData, setRowData] = useState<Record<string, any>[]>([]);
    const [requestId, setRequestId] = useState<string>();
    const [noResults, setNoResults] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [fetchErrorMessage, setFetchErrorMessage] = useState<string | null>(null);
    const errorDictionary = useErroresLibranzasDictionary();

    // guarda la clave de la última empresa cargada para evitar re-fetch
    // si el contexto oscila por actualizaciones asíncronas de Gigya
    const fetchedForRef = useRef<string | null>(null);

    // snapshot inmutable de los datos originales de la API — permite a Cancelar
    // revertir ediciones manuales, cargas de Excel y eliminaciones de filas
    const originalRowDataRef = useRef<Record<string, any>[]>([]);

    // extraer primitivos para evitar que cambios de referencia en el objeto business
    // disparen effects que usan companyDocID/companyDocType como dependencias
    const companyDocID = business?.get?.companyDocID;
    const companyDocType = business?.get?.companyDocType;

    const getStatusClass = useCallback((status: string) => {
        const map: Record<string, string> = {
            'AL DÍA': 'c-datatable-main__status--green',
            'Pendiente': 'c-datatable-main__status--orange',
            'EN MORA': 'c-datatable-main__status--red',
        };
        return map[status] ?? 'c-datatable-main__status--red';
    }, []);

    const [selectedRows, setSelectedRows] = useState<boolean[]>(
        new Array(rowData.length).fill(false)
    );

    // derivado — nunca queda desincronizado con selectedRows
    const selectAll = useMemo(
        () => selectedRows.length > 0 && selectedRows.every(Boolean),
        [selectedRows]
    );

    const handleSelectAll = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked;
            setSelectedRows(new Array(rowData.length).fill(checked));
        },
        [rowData.length]
    );

    const handleRowSelect = useCallback(
        (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked;
            setSelectedRows((prev) => prev.map((val, i) => (i === idx ? checked : val)));
        },
        []
    );

    const handleValorAPagarChange = useCallback((id: string, value: string) => {
        const numeric = value.replace(/[^0-9]/g, '');
        setRowData((prev) =>
            prev.map((row) =>
                row.id === id ? { ...row, valorAPagar: numeric } : row
            )
        );
    }, []);

    const handleDelete = useCallback(
        (row: Record<string, any>) => {
            setRowData((prev) => prev.filter((r) => r.id !== row.id));
            setSelectedRows((prev) => prev.filter((_, i) => rowData[i]?.id !== row.id));
        },
        [rowData]
    );

    const staticColumns = useMemo(
        () => [
            { accessorKey: 'id', header: 'No. de crédito' },
            { accessorKey: 'tipo', header: 'Tipo de doc.' },
            { accessorKey: 'documentoNumero', header: 'No. de doc.' },
            {
                accessorKey: 'nombre',
                header: 'Nombre',
                cell: ({ row }: { row: Record<string, any> }) => {
                    const val: string = row.nombre ?? '';
                    return val ? val.charAt(0).toUpperCase() + val.slice(1) : val;
                },
            },
            {
                accessorKey: 'fechaLimitePago',
                header: 'Límite de pago',
                cell: ({ row }: { row: Record<string, any> }) => {
                    const val: string = row.fechaLimitePago ?? '';
                    return val ? val.charAt(0).toUpperCase() + val.slice(1) : val;
                },
            },
            {
                accessorKey: 'saldo',
                header: 'Saldo',
                cell: ({ row }: { row: Record<string, any> }) =>
                    formatCurrency(row.saldo ?? ''),
            },
            {
                accessorKey: 'cuota',
                header: 'Cuota',
                cell: ({ row }: { row: Record<string, any> }) =>
                    formatCurrency(row.cuota ?? ''),
            },
            {
                accessorKey: 'valorAPagar',
                header: 'Valor a pagar',
                cell: ({ row }: { row: Record<string, any> }) => (
                    <input
                        type="text"
                        value={row.valorAPagar ?? ''}
                        onChange={(e) =>
                            handleValorAPagarChange(row.id, e.target.value)
                        }
                        className="c-datatable-main__input--valor"
                    />
                ),
            },
            {
                accessorKey: 'estado',
                header: 'Estado',
                cell: ({ row }: { row: Record<string, any> }) => {
                    const statusClass = getStatusClass(row.estado ?? '');
                    return (
                        <span className={`c-datatable-main__status ${statusClass}`}>
                            {row.estado ? row.estado.charAt(0).toUpperCase() + row.estado.slice(1) : ''}
                        </span>
                    );
                },
            },
        ],
        [handleValorAPagarChange]
    );

    const selectColumn = useMemo(
        () => ({
            accessorKey: 'select',
            header: (
                <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                />
            ),
            cell: ({ row }: { row: Record<string, any> }) => row.select,
        }),
        [selectAll, handleSelectAll]
    );

    const head = useMemo(
        () => [selectColumn, ...staticColumns],
        [selectColumn, staticColumns]
    );

    const data = useMemo(
        () =>
            rowData.map((row, idx) => ({
                select: (
                    <input
                        type="checkbox"
                        checked={selectedRows[idx] ?? false}
                        onChange={handleRowSelect(idx)}
                    />
                ),
                ...row,
            })),
        [rowData, selectedRows]
    );

    const totalValorAPagarSeleccionado = useMemo(() => {
        return rowData.reduce((acc, row, idx) => {
            if (selectedRows[idx]) {
                const digits = (row.valorAPagar ?? '').toString().replace(/\D/g, '');
                return acc + (Number(digits) || 0);
            }
            return acc;
        }, 0);
    }, [rowData, selectedRows]);


    const [showCargaMasiva, setShowCargaMasiva] = useState(false);
    const handleLoadFile = () => {
        setShowCargaMasiva(true);
    };

    const rowDataRef = useRef(rowData);
    rowDataRef.current = rowData;

    const handleCargaMasivaSuccess = useCallback(
        (data: any) => {
            const filas: any[] = data?.filas ?? [];
            const rid: string = data?.requestId ?? '';
            const rows = mergeCargarExcelFilasIntoRows(rowDataRef.current, filas);
            setRowData(rows);
            setRequestId(rid);
            setSelectedRows(new Array(rows.length).fill(true));
            setShowCargaMasiva(false);
        },
        []
    );

    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadToast, setDownloadToast] = useState<React.ReactNode>(null);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [currentStep, setCurrentStep] = useState<0 | 1>(0);
    const [isConsulta, setIsConsulta] = useState(false);
    const [historialFetched, setHistorialFetched] = useState(false);
    const [historialConsultaParams, setHistorialConsultaParams] = useState<{ tipoDocumento: string; numDocumento: string; fechaInicio: string; fechaFin: string }>({ tipoDocumento: '', numDocumento: '', fechaInicio: '', fechaFin: '' });
    const { rows: historialRows, loading: historialLoading, notFound: historialNotFound, fetch: fetchHistorial } = useHistorialLibranzas();
    const [email, setEmail] = useState('');
    const [celular, setCelular] = useState('');

    const {
        isPaying, payBlocked, payMessage, setPayMessage, setPayBlocked,
        payBlockedInfo, setPayBlockedInfo,
        urlToPay,
        showConfirmacionPago, isPagoRepetido,
        handleCancelConfirmacionPago,
        pagar,
    } = useLibranzasPago();

    // guard contra doble submit de pagar() por doble clic antes de que pagarDisabled reaccione
    const isSubmittingRef = useRef(false);

    const irAPasarela = useCallback(() => {
        if (!urlToPay) return;
        window.location.href = urlToPay;
    }, [urlToPay]);

    // Sincroniza email/celular cuando gigyaAccount resuelve de forma asíncrona
    const gigyaEmail = gigyaContext.gigyaAccount?.profile.email;
    const gigyaMobile = gigyaContext.gigyaAccount?.data.mobile;
    useEffect(() => {
        if (gigyaEmail) setEmail(gigyaEmail);
        if (gigyaMobile) setCelular(gigyaMobile);
    }, [gigyaEmail, gigyaMobile]);

    const downloadExcelFile = (base64: string, fileName: string) => {
        const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const handleDescargarArchivo = useCallback(() => {
        if (!requestId) return;
        setIsDownloading(true);
        setDownloadToast(null);
        getExcelLibranzas(requestId)
            .then((response) => {
                const base64 = response?.data?.data?.base64Content;
                if (!base64) {
                    setDownloadToast(
                        <GenericToast
                            setActiveToast={setDownloadToast}
                            isOk={false}
                            message="No se pudo generar el archivo. Por favor intente de nuevo."
                            position="up-right"
                        />
                    );
                    return;
                }
                const fileName = response?.data?.data?.fileName ?? 'libranzas.xlsx';
                downloadExcelFile(base64, fileName);
                setDownloadToast(
                    <GenericToast
                        setActiveToast={setDownloadToast}
                        isOk
                        message="El archivo se descargó correctamente."
                        position="up-right"
                    />
                );
            })
            .catch(() => {
                setDownloadToast(
                    <GenericToast
                        setActiveToast={setDownloadToast}
                        isOk={false}
                        message="Ocurrió un error al descargar el archivo. Por favor intente de nuevo."
                        position="up-right"
                    />
                );
            })
            .finally(() => {
                setIsDownloading(false);
            });
    }, [requestId]);

    const handleDescargarTransaccion = useCallback(async (transactionId: string | number) => {
        const response = await downloadHistorialExcelLibranzas(transactionId);
        const base64 = response?.data?.data?.base64Content;
        if (!base64) {
            throw new Error('No se pudo generar el archivo');
        }
        const fileName = response?.data?.data?.fileName ?? `transaccion-${transactionId}.xlsx`;
        downloadExcelFile(base64, fileName);
    }, []);

    // extraer primitivos del objeto gigyaContext para que el effect no se dispare
    // con cada actualización de referencia del contexto (refresco de token Gigya)
    const gigyaUID = gigyaContext?.gigyaAccount?.UID ?? null;
    useEffect(() => {
        if (!gigyaUID) {
            setShowLogin(true);
            setOpenBusinessModal(false);
        } else {
            setShowLogin(false);
            const tieneEmpresa = !!(companyDocID && companyDocType);
            setEmpresaSeleccionada(tieneEmpresa);
            setOpenBusinessModal(!tieneEmpresa);
        }
    }, [gigyaUID, companyDocID, companyDocType]);

    const handleHistorialBuscar = useCallback(
        ({ tipoDocumento, numDocumento, fechaInicio, fechaFin }: { tipoDocumento: string; numDocumento: string; fechaInicio: string; fechaFin: string }) => {
            setIsConsulta(false);
            setHistorialFetched(true);
            setHistorialConsultaParams({ tipoDocumento, numDocumento, fechaInicio, fechaFin });
            fetchHistorial({
                buyerDocumentCompany: companyDocID ?? '',
                buyerDocumentTypeCompany: companyDocType ?? '',
                ...(tipoDocumento && numDocumento
                    ? { userDocumentType: tipoDocumento, userDocumentNumber: numDocumento }
                    : {}),
                ...(fechaInicio && fechaFin ? { fechaInicio, fechaFin } : {}),
            });
        },
        [companyDocID, companyDocType, fetchHistorial]
    );

    const handleConsultaChange = useCallback((value: boolean) => {
        setIsConsulta(value);
        setHistorialFetched(false);
    }, []);

    const isConsultaPorNIT = !historialConsultaParams.tipoDocumento && !historialConsultaParams.numDocumento;

    const handleCancelar = useCallback(() => {
        const original = originalRowDataRef.current;
        setRowData(original);
        setSelectedRows(new Array(original.length).fill(false));
        setCurrentStep(0);
    }, []);

    const handlePagar = useCallback(() => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        const account = gigyaContext.gigyaAccount;
        pagar(
            {
                document: account?.data.docNumber ?? '',
                documentType: account?.data.docType ?? '',
                documentCompany: companyDocID ?? '',
                documentTypeCompany: companyDocType ?? '',
                email,
                mobile: celular,
                name: account?.profile.firstName ?? '',
                surname: account?.profile.lastName ?? '',
            },
            rowData
                .filter((_, idx) => selectedRows[idx])
                .map((row) => ({
                    reference: row.id as string,
                    total: Number((row.valorAPagar ?? '').toString().replace(/\D/g, '')) || 0,
                })),
            requestId
        );
    }, [rowData, selectedRows, gigyaContext.gigyaAccount, companyDocID, companyDocType, email, celular, pagar, requestId]);

    // libera el guard cuando termina la petición (éxito o error), permitiendo un nuevo intento
    useEffect(() => {
        if (!isPaying) isSubmittingRef.current = false;
    }, [isPaying]);

    useEffect(() => {
        if (!companyDocID || !companyDocType) return;

        // guard: si ya se cargaron datos para esta empresa no volver a fetchear
        // evita re-cargas por oscilaciones del contexto (refresco de token Gigya)
        const key = `${companyDocType}:${companyDocID}`;
        if (fetchedForRef.current === key) return;
        fetchedForRef.current = key;

        setNoResults(false);
        setFetchErrorMessage(null);
        setIsFetching(true);
        getLibranzasEmpresas(companyDocType, companyDocID)
            .then((response) => {
                const { creditos, requestId: rid } = response.data?.data ?? {};
                const rows = creditos?.length ? adaptLibranzasCreditosToRows(creditos) : [];
                originalRowDataRef.current = rows;
                setRequestId(rid);
                setRowData(rows);
                setSelectedRows(new Array(rows.length).fill(false));
                setNoResults(rows.length === 0);
            })
            .catch((error) => {
                setFetchErrorMessage(
                    resolveLibranzasErrorMessage(
                        errorDictionary,
                        error,
                        'Ocurrió un error al cargar los créditos. Por favor intente de nuevo.'
                    )
                );
            })
            .finally(() => {
                setIsFetching(false);
            });
    }, [companyDocID, companyDocType, errorDictionary]);

    return (
        <div className="t-pago-creditos-libranzas">
            {showLogin ? (
                <LoginRequest />
            ) : !empresaSeleccionada ? (
                <BusinessModal
                    closeModal={() => setOpenBusinessModal(false)}
                    isMobile
                    setShowConfirmation={() => {}}
                    isPrivate={false}
                    closeModalPermissionPage={() => setOpenBusinessModal(false)}
                    serviceId={templateData?.serviceId}
                />
            ) : (
                <>
                    <CabeceraLibranzas
                        nombre={nombre}
                        imagenUrl={fileUrl}
                        imgTitle={imgTitle}
                        descripcion={descripcion}
                        numDocumentoEmpresa={Number.parseInt(
                            business?.get?.companyDocID ?? '0'
                        )}
                        razonSocial={business?.get?.label || ''}
                        formTitle={'Datos de la empresa'}
                        breadcrumb={breadcrumbData}
                        interrogationDescription={interrogationDescription}
                        realizadosImagenUrl={realizadosImagenUrl}
                        realizadosImgTitle={realizadosImgTitle}
                        realizadosDescripcion={realizadosDescripcion}
                        onConsultaChange={handleConsultaChange}
                        onBuscar={handleHistorialBuscar}
                    />
                    {!isConsulta && !historialFetched && (
                        <PaymentStepper
                            itemList={['Selecciona las Libranzas', 'Resumen del pago']}
                            currentIndex={currentStep}
                        />
                    )}
                    <div className="t-pago-creditos-libranzas__view-content">
                    {historialFetched ? (
                        <>
                            <LibranzasHistorial
                                rows={historialRows}
                                loading={historialLoading}
                                notFound={historialNotFound}
                                showDownloadButton={isConsultaPorNIT}
                                onDownloadTransaction={handleDescargarTransaccion}
                            />
                        </>
                    ) : isConsulta ? (
                        <>
                            <div className="t-pago-creditos-libranzas__no-results">
                                <Typography variant="body1">
                                    Completa los filtros de búsqueda en la sección anterior para consultar el histórico de pagos.
                                </Typography>
                            </div>
                        </>
                    ) : fetchErrorMessage ? (
                        <>
                            <div className="t-pago-creditos-libranzas__no-results">
                                <Typography variant="body1">
                                    {fetchErrorMessage}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="secundary"
                                    onClick={() => setOpenBusinessModal(true)}
                                >
                                    Cambiar empresa
                                </Button>
                            </div>
                        </>
                    ) : noResults && !isFetching ? (
                        <>
                            <div className="t-pago-creditos-libranzas__no-results">
                                <Typography variant="body1">
                                    No se encontraron créditos de libranza para esta empresa.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="secundary"
                                    onClick={() => setOpenBusinessModal(true)}
                                >
                                    Cambiar empresa
                                </Button>
                            </div>
                        </>
                    ) : currentStep === 0 ? (
                        <>
                            <DataTable
                                data={data}
                                head={head}
                                loading={isFetching}
                                onDelete={handleDelete}
                            />
                            <div className="t-pago-creditos-libranzas__button-row">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleDescargarArchivo}
                                    disabled={!requestId || isDownloading}
                                >
                                    {isDownloading ? 'Descargando...' : 'Descargar archivo'}
                                </Button>
                                <Button
                                    onClick={handleLoadFile}
                                    variant="outlined"
                                >
                                    Subir archivo
                                </Button>
                            </div>
                            {downloadToast}
                            <div className="t-pago-creditos-libranzas__total-section">
                                <h4 className="t-pago-creditos-libranzas__total-label">
                                    Total a pagar:{' '}
                                    {formatCurrency(
                                        totalValorAPagarSeleccionado.toString()
                                    )}
                                </h4>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setCurrentStep(1)}
                                    disabled={!selectedRows.some(Boolean)}
                                >
                                    Continuar
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <LibranzasResumenPago
                                documentoEmpresa={companyDocID ?? ''}
                                nombreEmpresa={business?.get?.label ?? ''}
                                total={totalValorAPagarSeleccionado}
                                email={email}
                                onEmailChange={setEmail}
                                celular={celular}
                                onCelularChange={setCelular}
                                termsAccepted={termsAccepted}
                                onTermsToggle={() => setTermsAccepted(!termsAccepted)}
                                onBack={() => setCurrentStep(0)}
                                onCancel={handleCancelar}
                                onGoToConfirmacion={handlePagar}
                                pagarDisabled={
                                    !isValidEmail(email) ||
                                    celular.length !== 10 ||
                                    !termsAccepted ||
                                    isPaying ||
                                    payBlocked
                                }
                                title={nombre}
                                paymentMethodRefs={paymentMethodRefs}
                                paymentHeadInfo={paymentHeadInfo}
                            />
                        </>
                    )}
                    </div>
                </>
            )}
            {showCargaMasiva && empresaSeleccionada && (
                <Modal open={showCargaMasiva} onClose={() => setShowCargaMasiva(false)}>
                    <div className="t-pago-creditos-libranzas__modal-content">
                        <h2 className="t-pago-creditos-libranzas__modal-title">
                            Subir archivo
                        </h2>
                        <LibranzaCargaMasiva
                            onSuccess={handleCargaMasivaSuccess}
                            interrogationDescription={interrogationDescription}
                        />
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setShowCargaMasiva(false)}
                            className="t-pago-creditos-libranzas__modal-close-btn"
                        >
                            Cerrar
                        </Button>
                    </div>
                </Modal>
            )}
            {empresaSeleccionada && openBusinessModal && (
                <BusinessModal
                    closeModal={() => setOpenBusinessModal(false)}
                    isMobile
                    setShowConfirmation={() => {}}
                    isPrivate={false}
                    closeModalPermissionPage={() => setOpenBusinessModal(false)}
                    serviceId={templateData?.serviceId}
                />
            )}
            {isPaying && (
                <Modal open={isPaying} onClose={() => {}}>
                    <div className="t-pago-creditos-libranzas__loading-content">
                        <Loader size="__big" />
                        <Typography variant="body1">Procesando pago...</Typography>
                    </div>
                </Modal>
            )}
            {(!!payMessage || !!payBlockedInfo) && (
                <Modal
                    open={!!payMessage || !!payBlockedInfo}
                    onClose={() => {
                        setPayMessage(null);
                        setPayBlockedInfo(null);
                        setPayBlocked(false);
                    }}
                >
                    <div className="t-pago-creditos-libranzas__pay-error-content">
                        {payBlockedInfo ? (
                            <>
                                <Typography variant="body1">{`El Nit ${payBlockedInfo.nit} tiene un pago en proceso.`}</Typography>
                                <Typography variant="body1">Referencia: pago de libranzas.</Typography>
                            </>
                        ) : (
                            <Typography variant="body1">{payMessage}</Typography>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                setPayMessage(null);
                                setPayBlockedInfo(null);
                                setPayBlocked(false);
                            }}
                        >
                            Cerrar
                        </Button>
                    </div>
                </Modal>
            )}
            <LibranzasConfirmacionPago
                open={showConfirmacionPago}
                cancelarLabel={confirmacionBotones.cancelar}
                continuarLabel={confirmacionBotones.continuar}
                onCancelar={handleCancelConfirmacionPago}
                onContinuar={irAPasarela}
                titulo={confirmacionTitulo}
                descripcion={confirmacionDescripcion}
                imagenUrl={confirmacionImagenUrl}
                advertencia={isPagoRepetido ? PAGO_REPETIDO_ADVERTENCIA : undefined}
            />
        </div>
    );
};
